import { CentraSelectionItem } from "@/features/cart/cartStore";
import { ProductVariant } from "@/features/product/types";
import { FormattedCartItem } from "@/lib/centra/formatters/formatSelection";
import { ofetch } from "ofetch";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type SizeChangeEvent = CustomEvent<{ size: string }>;
export const SIZE_CHANGE_EVENT = "size-change";

export function dispatchSizeChangeEvent(size: string) {
  const event = new CustomEvent(SIZE_CHANGE_EVENT, {
    detail: { size },
  });
  document.dispatchEvent(event);
}

export const useEasySize = (
  config: z.infer<typeof easySizeOptionsSchema>,
  { enabled = false }
) => {
  const easySize = useRef<EasySize | null>(null);

  useEffect(() => {
    if (typeof EasySize === "undefined" || !enabled) return;

    easySize.current = new EasySize(config);
    easySize.current.start();
    return;
  }, [enabled, config]);

  useEffect(() => {
    const handleSizeChange = (event: Event) => {
      easySize.current?.sizeChangedTo((event as SizeChangeEvent).detail.size);
    };

    document.addEventListener(SIZE_CHANGE_EVENT, handleSizeChange);

    return () => {
      document.removeEventListener(SIZE_CHANGE_EVENT, handleSizeChange);
    };
  }, []);

  return easySize;
};

declare global {
  class EasySize {
    constructor(args: EasySizeOptions);
    start(): void;
    sizeChangedTo(size: string): void;
    open(opts: Record<string, unknown>): void;
  }
}
export type EasySizeOptions = z.infer<typeof easySizeOptionsSchema>;

export const easySizeOptionsSchema = z.object({
  product_id: z.string(),
  product_type: z.string(),
  product_brand: z.string(),
  product_gender: z.string().optional(),
  product_sku: z.string().optional(),
  product_model: z.string(),
  image_url: z.string(),
  sizes_in_stock: z.record(z.number()),
  shop_id: z.string(),
  user_id: z.string().optional().default("-1"),
  placeholder: z.string(),
  recommendation_placeholder: z.string(),
  add_to_cart_btn_selector: z.string(),
  size_selector: z.string(),
  cookies_enabled: z.boolean().optional(),
  loaded: z.function().optional(),
});

interface EasySizeCreateProps {
  activeVariant?: ProductVariant;
  productId: string;
  addToCartId: string;
}

const defaultOptions = {
  product_brand: "blaest",
  shop_id: "9381410657",
} as const;

export const createEasySizeConfig = ({
  activeVariant,
  addToCartId,
  productId,
}: EasySizeCreateProps) => {
  if (!activeVariant) return null;

  const conf: Partial<EasySizeOptions> = {
    ...defaultOptions,
    product_id: productId,
    product_type: activeVariant.type,
    product_gender: activeVariant.gender,
    product_model: activeVariant.fit,
    image_url: activeVariant.media?.at(0)?.href,
    sizes_in_stock: activeVariant.sizes?.reduce(
      (acc, size) => {
        if (size.stock) {
          acc[size.name] = 1;
        } else {
          acc[size.name] = 0;
        }
        return acc;
      },
      {} as { [size: string]: number }
    ),
    user_id: "-1",
    placeholder: "#product_size",
    recommendation_placeholder: "#product_recommendation",
    add_to_cart_btn_selector: `#${addToCartId}`,
    size_selector: "#size_dropdown",
  };

  const parsed = easySizeOptionsSchema.safeParse(conf);

  if (parsed.success) return parsed.data;

  console.log(
    `Cannot create easysize config object for ${productId} - ${activeVariant.name}`,
    parsed.error.errors
  );
  return null;
};

type EasySizeProduct = {
  purchased_size: string;
  pageview_id: string;
  order_id: string;
  product_id: string;
  created_at: string;
  user_id: string;
  quantity: number;
  price: number;
  price_currency: string;
  test_order?: boolean;
};

type OrderData = Pick<
  EasySizeProduct,
  "created_at" | "user_id" | "order_id" | "price_currency"
>;

const generateEasySizeProduct = (
  orderData: OrderData,
  item: CentraSelectionItem & { uri: string; discountedPrice: string }
): EasySizeProduct | null => {
  if (!item.sku?.length) return null;
  const pageview_id =
    useEasySizeStore.getState().getPageView(item?.uri) ?? undefined;

  return {
    quantity: item?.quantity ?? 0,
    price:
      parseInt(item?.discountedPrice.split(orderData.price_currency)[0]) ?? 0,
    price_currency: orderData.price_currency,
    purchased_size: item?.size ?? "",
    pageview_id: pageview_id || "",
    order_id: orderData.order_id,
    product_id: item.sku ?? "",
    created_at: orderData.created_at, // '%Y-%m-%d %H:%M:%S'
    user_id: orderData.user_id,
  };
};
export const trackEasySizePurchases = async (
  orderData: OrderData,
  items: FormattedCartItem[]
) => {
  const url = `https://tracking.easysize.me/analytics/${defaultOptions.shop_id}/order`;
  const body = JSON.stringify({
    products: items
      .map((item) => generateEasySizeProduct(orderData, item))
      .filter(Boolean),
  });

  try {
    const res = await ofetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    console.log(body);
    console.log("Successfully tracked purchases:", res);
  } catch (error) {
    console.error("Failed to track EasySize purchases:", error);
    throw error;
  }
};

interface EasySizeStore {
  pageViews: Record<string, string>;
  trackPageView: (sku: string, pageview_id: string) => void;
  getPageView: (sku: string) => string | null;
}

export const useEasySizeStore = create<EasySizeStore>()(
  persist(
    (set, get) => ({
      pageViews: {},

      trackPageView: (sku, pageview_id) => {
        set((state) => {
          const updatedPageViews = { ...state.pageViews, [sku]: pageview_id };

          const keys = Object.keys(updatedPageViews);
          if (keys.length > 30) {
            delete updatedPageViews[keys[0]];
          }

          return { pageViews: updatedPageViews };
        });
      },

      getPageView: (sku) => get().pageViews[sku] || null,
    }),
    {
      name: "easySizePageViews",
    }
  )
);
